import React, { useEffect, useState, useRef, useContext } from 'react';
import { GiftRedemption, SideSheet } from '@desquared/wave-components-library';
import { GiftsSideSheetStore } from '@/store/sideSheet';
import { DeliveryServicesContext } from '@/contexts/DeliveryServicesContext.jsx';
import { getShopsForDeliveryV3 } from '@/api/Shops';
import { Shop } from '@/store/shop';
import { getStoresList, transformShops, transformShopsWithDistance } from '@/utils/storeSelector';
import { useTranslations } from 'next-intl';
import { useQuery } from '@tanstack/react-query';
import { DistanceContext } from '@/contexts/DistanceUomContext';

export default function GiftsAside({
  firstName,
  lastName,
  onConfirmRedemption,
  alertText,
  onSelectDeliveryOption,
  onSelectStore,
  comment,
  onChangeComment,
  onSelectAllStores,
  deliveryOptions,
  onClose,
  showStores,
}) {
  const refOne = useRef(null);
  const t = useTranslations('gifts');
  const r = useTranslations('common.asides.store_selector');

  const [selectedStoreIndex, setSelectedStoreIndex] = useState(Shop.getValue()?.selectedShop ?? undefined);
  const { selectedShop, servicesV3 } = useContext(DeliveryServicesContext);
  const [shops, setShops] = useState([]);
  const { unitShorthand } = useContext(DistanceContext);

  async function getTakeawayStores() {
    if (Shop.getValue()?.[servicesV3.find(s => !s.deliversToAddress).id])
      return Shop.getValue()?.[servicesV3.find(s => !s.deliversToAddress).id];

    return await getShopsForDeliveryV3(servicesV3.find(s => !s.deliversToAddress).id);
  }

  function getBrowserLocation() {
    return new Promise((res, rej) => {
      if (navigator && navigator.geolocation) navigator.geolocation.getCurrentPosition(res, rej);
    });
  }

  const { data: takeawayStores, isLoading: isTakeawayStoresLoading } = useQuery({
    queryKey: ['takeaway-stores', servicesV3],
    queryFn: getTakeawayStores,
    enabled: !!servicesV3.length,
  });

  useEffect(() => {
    async function createStores(data) {
      let transformedShops;
      try {
        const position = await getBrowserLocation();
        transformedShops = transformShops(data, { lat: position.coords.latitude, lng: position.coords.longitude });
      } catch (e) {
        transformedShops = transformShops(data);
      }

      return transformedShops;
    }

    if (takeawayStores)
      createStores(takeawayStores)
        .then(data => {
          // Pre select shop if already selected for takeaway
          if (selectedShop?._id) setSelectedStoreIndex(takeawayStores.findIndex(i => i?._id === selectedShop?._id));

          const { enhancedStores } = getStoresList(data, selectedStoreIndex, unitShorthand);

          setShops(enhancedStores);
        })
        .catch(error => console.log(error));
  }, [takeawayStores, setSelectedStoreIndex, selectedShop?._id, setShops]);

  const closeSheet = () => {
    setTimeout(() => {
      GiftsSideSheetStore.close();
    }, '300');
  };

  useEffect(() => {
    document.addEventListener('click', handleClickOutside, true);

    return () => {
      document.removeEventListener('click', handleClickOutside, false);
    };
  }, []);

  const handleClickOutside = e => {
    if (refOne.current && !refOne.current.contains(e.target)) {
      GiftsSideSheetStore.close();
    }
  };

  function findSelectedOption(options) {
    return options?.find(option => option.selected === true) || null;
  }

  return (
    <div ref={refOne}>
      <SideSheet
        header={t('aside.header')}
        hasClose={true}
        onClose={() => {
          closeSheet();
          onClose();
        }}
        buttonText={t('aside.redeem')}
        onSubmit={() => {
          onConfirmRedemption();
          closeSheet();
        }}
        isDisabled={
          findSelectedOption(deliveryOptions)?.type === 'TAKEAWAY' &&
          (selectedStoreIndex === null || selectedStoreIndex === '') &&
          !selectedShop?._id
        }
      >
        <GiftRedemption
          showAlert={true}
          alertText={alertText}
          deliveryOptionTitle={t('aside.delivery_options_title')}
          onSelectDeliveryOption={onSelectDeliveryOption}
          showAllStoresText={t('aside.show_all_stores')}
          storeOptionTitle={t('aside.store_option_title')}
          listAddress={shops}
          onSelectStore={i => {
            setSelectedStoreIndex(i);
            onSelectStore(shops[i]._id);
          }}
          commentTitle={t('aside.comment_title')}
          firstName={firstName}
          lastName={lastName}
          hasPic={false}
          comment={comment}
          onChangeComment={onChangeComment}
          deliveryOptions={deliveryOptions}
          showStores={showStores}
          onSelectAllStores={onSelectAllStores}
          selectedStoreIndex={selectedStoreIndex}
          openStoreLabelWording={r('open')}
          closedStoreLabelWording={r('closed')}
          closingSoonStoreLabelWording={r('closing')}
        />
      </SideSheet>
    </div>
  );
}
